/**
 * Tailwind CSS implementation of Token component
 * Uses Seeds token CSS classes from token.css
 */

import * as React from "react";
import { mergeRefs } from "@sproutsocial/seeds-react-utilities";
import { useTextContent } from "@sproutsocial/seeds-react-hooks";
import Box from "@sproutsocial/seeds-react-box";
import Icon from "@sproutsocial/seeds-react-icon";
import { VisuallyHidden } from "@sproutsocial/seeds-react-visually-hidden";
import type { TypeTokenProps } from "./TokenTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const TokenTailwind = ({
  children,
  closeable = true,
  onClick,
  qa,
  valid = true,
  hasWarning = false,
  isGenerated = false,
  disabled = false,
  palette = "neutral",
  innerRef = null,
  as,
  className,
  ...rest
}: TypeTokenProps) => {
  const textContainer = useTextContent("");

  const isInteractive = closeable || !!onClick;
  // Mirror the styled component: it is a styled.button rendered `as` a div when
  // the token is neither closeable nor clickable. A consumer-provided `as` still
  // wins, matching the original (where `as` arrived via {...rest} last).
  const Element = (as ??
    (isInteractive ? "button" : "div")) as React.ElementType;

  const classes = cn(
    "seeds-token",
    {
      "seeds-token-closeable": isInteractive,
      "seeds-token-blue": palette === "blue",
      "seeds-token-disabled": disabled,
      "seeds-token-invalid": !valid,
      "seeds-token-warning": hasWarning,
      "seeds-token-generated": isGenerated,
    },
    className
  );

  return (
    <Element
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore some legacy ref type mismatch between divs
      ref={mergeRefs(textContainer, innerRef)}
      type={isInteractive ? "button" : undefined}
      className={classes}
      {...(isInteractive ? { disabled } : {})}
      onClick={onClick}
      data-qa-token={textContainer.current}
      data-qa-token-iscloseable={closeable === true}
      data-qa-token-isvalid={valid === true}
      data-qa-token-isdisabled={disabled === true}
      {...qa}
      {...rest}
    >
      {!valid && (
        <>
          <VisuallyHidden as="div" role="status">
            This is an invalid token
          </VisuallyHidden>
          <Icon
            aria-hidden
            pr={300}
            name="circle-exclamation-solid"
            size="mini"
          />
          <VisuallyHidden>Invalid</VisuallyHidden>
        </>
      )}
      {closeable ? (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box as="span" display="flex" alignItems="center">
            {children}
          </Box>
          <Icon aria-hidden pl={300} name="x-solid" size="mini" />
        </Box>
      ) : (
        children
      )}
    </Element>
  );
};

TokenTailwind.displayName = "TokenTailwind";
